/**
 * Links a calendarios web (Google Calendar / Outlook.com) que abren el
 * formulario "nuevo evento" del proveedor ya prellenado — sin descargar
 * ningún archivo ni depender de OAuth.
 *
 * Complementa a `icsGenerator.ts`:
 *   - `.ics`  → Apple Calendar y Outlook de escritorio (necesitan el archivo).
 *   - estos links → usuarios de Google/Outlook web, que NO abren `.ics` en desktop.
 */

import { getEventBounds, type ICSEventInput } from './icsGenerator';

// YYYYMMDDTHHMMSSZ — formato compacto UTC que pide Google Calendar en `dates`.
function toCompactUTC(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

// ISO 8601 con sufijo Z — formato que pide el deeplink de Outlook.
function toISOStringUTC(date: Date): string {
  return date.toISOString().split('.')[0] + 'Z';
}

/**
 * URL de "Agregar a Google Calendar". Abre calendar.google.com con el evento
 * cargado (título, fecha/hora, descripción, ubicación) listo para guardar.
 */
export function buildGoogleCalendarUrl(event: ICSEventInput): string {
  const { start, end } = getEventBounds(event);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toCompactUTC(start)}/${toCompactUTC(end)}`,
  });
  if (event.description) params.set('details', event.description);
  if (event.location) params.set('location', event.location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * URL de "Agregar a Outlook" (cuentas Outlook.com / Hotmail / Live).
 * Abre el compositor de eventos de Outlook web prellenado.
 */
export function buildOutlookCalendarUrl(event: ICSEventInput): string {
  const { start, end } = getEventBounds(event);

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: toISOStringUTC(start),
    enddt: toISOStringUTC(end),
  });
  if (event.description) params.set('body', event.description);
  if (event.location) params.set('location', event.location);

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}
